import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  IndianRupee,
  RefreshCw,
  Search,
  X,
  Gift,
  Plus,
  Trash2,
  Edit3,
  Calendar,
  Phone,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin: "bg-amber-500/15 text-amber-400",
  manager: "bg-blue-500/15 text-blue-400",
  billing: "bg-emerald-500/15 text-emerald-400",
  staff: "bg-stone-500/15 text-stone-400",
};

// ── Utility Functions ─────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Returns "YYYY-MM" for the current month
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// ── Month Picker ──────────────────────────────────────────────
const MonthPicker = ({ value, onChange }) => {
  // value: "YYYY-MM" | "" (empty = all time)
  const monthLabel = (val) => {
    if (!val) return "All Time";
    const [y, m] = val.split("-");
    return new Date(Number(y), Number(m) - 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  const step = (dir) => {
    if (!value) return;
    const [y, m] = value.split("-").map(Number);
    const d = new Date(y, m - 1 + dir);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    onChange(next);
  };

  const isCurrentMonth = value === currentMonth();
  const isFuture =
    value && new Date(value + "-01") > new Date(currentMonth() + "-01");

  return (
    <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-xl shadow-sm px-2 py-1.5">
      {/* Previous month */}
      <button
        onClick={() => step(-1)}
        disabled={!value}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-30 transition-all"
        title="Previous month"
      >
        <ChevronLeft size={15} />
      </button>

      {/* Native month input hidden, label shown */}
      <div className="relative flex items-center gap-2 px-1">
        <Calendar size={13} className="text-amber-500 shrink-0" />
        <input
          type="month"
          value={value}
          max={currentMonth()}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer w-full"
          title="Pick a month"
        />
        <span className="text-sm font-bold text-stone-700 whitespace-nowrap select-none min-w-[130px] text-center">
          {monthLabel(value)}
        </span>
      </div>

      {/* Next month */}
      <button
        onClick={() => step(1)}
        disabled={!value || isCurrentMonth || isFuture}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-30 transition-all"
        title="Next month"
      >
        <ChevronRight size={15} />
      </button>

      {/* Toggle: All Time / current month */}
      {value ? (
        <button
          onClick={() => onChange("")}
          className="ml-1 h-7 px-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 text-[11px] font-bold transition-all whitespace-nowrap"
          title="Show all time"
        >
          All Time
        </button>
      ) : (
        <button
          onClick={() => onChange(currentMonth())}
          className="ml-1 h-7 px-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-600 text-[11px] font-bold border border-amber-200 transition-all whitespace-nowrap"
          title="Jump to this month"
        >
          This Month
        </button>
      )}
    </div>
  );
};

// ── Add/Edit Commission Modal ─────────────────────────────────
const CommissionModal = ({
  order,
  staff,
  existingCommission,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState({
    commission_type: existingCommission?.commission_type || "flat",
    amount: existingCommission?.amount || "",
    percentage_value: existingCommission?.percentage_value || "",
    notes: existingCommission?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.commission_type === "percentage" && form.percentage_value) {
      const calc = (order.total * parseFloat(form.percentage_value)) / 100;
      set("amount", calc.toFixed(2));
    }
  }, [form.percentage_value, form.commission_type, order.total]);

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        staff_id: staff.id,
        amount: parseFloat(form.amount),
        commission_type: form.commission_type,
        percentage_value: form.percentage_value
          ? parseFloat(form.percentage_value)
          : null,
        order_id: order.id,
        order_amount: order.total,
        notes: form.notes,
        date:
          order.invoice_date ||
          new Date(order.created_at).toISOString().split("T")[0],
      });
      ``;
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-xl">
              <Gift size={17} className="text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                {existingCommission ? "Edit" : "Add"} Commission
              </h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Order {order.invoice_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="bg-white/5 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <span className="text-sm font-black text-amber-400">
                  {staff.name?.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-sm font-bold text-white">{staff.name}</p>
                <p className="text-xs text-stone-500">{staff.email}</p>
              </div>
            </div>
            <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-stone-500">Order Total:</span>
                <span className="ml-1 text-white font-bold">
                  {formatCurrency(order.total)}
                </span>
              </div>
              <div>
                <span className="text-stone-500">Customer:</span>
                <span className="ml-1 text-white font-bold">
                  {order.customer_name || "Walk-in"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-2 block">
              Commission Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "flat", label: "Flat Amount", desc: "Fixed ₹ reward" },
                {
                  value: "percentage",
                  label: "Percentage",
                  desc: "% of order",
                },
              ].map((ct) => {
                const isActive = form.commission_type === ct.value;
                return (
                  <button
                    key={ct.value}
                    onClick={() => set("commission_type", ct.value)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                      isActive
                        ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
                        : "border-white/10 text-stone-500 hover:border-white/20"
                    }`}
                  >
                    <div>
                      <p className="text-xs font-bold">{ct.label}</p>
                      <p className="text-[10px] opacity-60">{ct.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {form.commission_type === "percentage" && (
            <div>
              <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1.5 block">
                Commission Percentage
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.percentage_value}
                  onChange={(e) => set("percentage_value", e.target.value)}
                  placeholder="e.g. 5"
                  className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 pr-8 text-white text-sm outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
                  %
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1.5 block">
              Commission Amount *
              {form.commission_type === "percentage" && form.amount && (
                <span className="ml-2 text-amber-400 font-normal normal-case">
                  auto-calculated
                </span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400 font-bold">
                ₹
              </span>
              <input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder="0.00"
                className="w-full h-12 bg-white/5 border border-white/10 rounded-xl pl-8 pr-4 text-white text-lg font-mono font-black outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-700"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-1.5 block">
              Notes (Optional)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
              placeholder="Reason for commission..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-stone-300 text-sm resize-none outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white hover:border-white/20 text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw size={13} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Gift size={14} />
                {existingCommission ? "Update" : "Add"} Commission
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Pagination Component ──────────────────────────────────────
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPerPageChange,
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;

  const pageNumbers = useMemo(() => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-stone-200">
      <div className="flex items-center gap-3">
        <span className="text-xs text-stone-500">
          Showing {startIndex + 1}–
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          staff
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          className="h-8 px-2 rounded-lg border border-stone-200 bg-white text-stone-600 text-xs font-medium outline-none focus:ring-2 focus:ring-amber-500/30"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-400 hover:bg-stone-50 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronsLeft size={14} />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-400 hover:bg-stone-50 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeft size={14} />
        </button>

        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-all ${
              currentPage === page
                ? "bg-amber-500 text-white shadow-sm"
                : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50 hover:text-amber-600"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-400 hover:bg-stone-50 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronRight size={14} />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-stone-200 text-stone-400 hover:bg-stone-50 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};

// ── Staff Card ────────────────────────────────────────────────
const StaffCard = ({
  staff,
  orders,
  commissions,
  onAddCommission,
  onDeleteCommission,
  onEditCommission,
  index,
  allUsers,
}) => {
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderPage, setOrderPage] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(10);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingCommission, setEditingCommission] = useState(null);
  const toggleOrderExpand = (orderId) =>
    setExpandedOrders((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  const userDetails = allUsers.find((u) => u.id === staff.id) || staff;

  // const staffOrderIds = useMemo(
  //   () => new Set(orders.map((o) => o.id)),
  //   [orders],
  // );
  // const staffCommissions = commissions.filter((c) =>
  //   staffOrderIds.has(c.order_id),
  // );
  const staffCommissions = commissions.filter((c) => c.staff_id === staff.id);
  const totalEarned = staffCommissions.reduce((s, c) => s + c.amount, 0);
  const pendingAmount = staffCommissions
    .filter((c) => c.status === "pending")
    .reduce((s, c) => s + c.amount, 0);

  console.log("Staff Commissions:", staffCommissions, orders);

  const paidAmount = staffCommissions
    .filter((c) => c.status === "paid")
    .reduce((s, c) => s + c.amount, 0);

  const totalRevenue = orders.reduce((s, o) => s + o.total, 0);
  const orderCount = orders.length;

  const filteredOrders = useMemo(() => {
    if (!orderSearch) return orders;
    const q = orderSearch.toLowerCase();
    return orders.filter(
      (o) =>
        o.invoice_number?.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_phone?.toLowerCase().includes(q),
    );
  }, [orders, orderSearch]);

  const totalOrderPages = Math.ceil(filteredOrders.length / orderPageSize);
  const pagedOrders = filteredOrders.slice(
    (orderPage - 1) * orderPageSize,
    orderPage * orderPageSize,
  );

  const handleAddCommission = (order) => {
    setSelectedOrder(order);
    setEditingCommission(null);
    setShowCommissionModal(true);
  };

  const handleEditCommission = (order, commission) => {
    setSelectedOrder(order);
    setEditingCommission(commission);
    setShowCommissionModal(true);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: index * 0.035,
          type: "spring",
          stiffness: 300,
          damping: 28,
        }}
        className="bg-white border border-stone-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      >
        <div
          className="px-5 py-4 cursor-pointer select-none"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-900/20">
                <span className="text-white font-black text-lg">
                  {staff.name?.charAt(0)}
                </span>
              </div>
              <div className="min-w-0">
                <h3 className="font-black text-stone-800 text-base truncate">
                  {staff.name}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      ROLE_COLORS[userDetails.role] ||
                      "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {userDetails.role}
                  </span>
                  <span className="text-xs text-stone-500">
                    {userDetails.email}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className="text-[11px] text-stone-500 flex items-center gap-1">
                    <ShoppingBag size={10} /> {orderCount} order
                    {orderCount !== 1 ? "s" : ""}
                  </span>
                  <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                    <IndianRupee size={10} />
                    {formatCurrency(totalRevenue)} revenue
                  </span>
                  <span className="text-[11px] text-amber-600 font-semibold flex items-center gap-1">
                    <Gift size={10} />
                    {formatCurrency(totalEarned)} earned
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
                <div className="text-right">
                  <p className="text-[9px] text-stone-500 font-bold uppercase tracking-wider">
                    Pending
                  </p>
                  <p className="text-amber-600 font-black text-sm">
                    {formatCurrency(pendingAmount)}
                  </p>
                </div>
                <div className="w-px h-8 bg-stone-200" />
                <div className="text-right">
                  <p className="text-[9px] text-stone-500 font-bold uppercase tracking-wider">
                    Paid
                  </p>
                  <p className="text-emerald-600 font-black text-sm">
                    {formatCurrency(paidAmount)}
                  </p>
                </div>
              </div>
              <div className="p-2 text-stone-500">
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-stone-100">
                <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search
                      size={13}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                    />
                    <input
                      value={orderSearch}
                      onChange={(e) => {
                        setOrderSearch(e.target.value);
                        setOrderPage(1);
                      }}
                      placeholder="Search orders by invoice, customer, phone..."
                      className="w-full h-8 bg-stone-50 border border-stone-200 rounded-lg pl-8 pr-8 text-xs text-stone-700 outline-none focus:ring-1 focus:ring-amber-500/40 placeholder-stone-400 transition-all"
                    />
                    {orderSearch && (
                      <button
                        onClick={() => {
                          setOrderSearch("");
                          setOrderPage(1);
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  <select
                    value={orderPageSize}
                    onChange={(e) => {
                      setOrderPageSize(Number(e.target.value));
                      setOrderPage(1);
                    }}
                    className="h-8 px-2 rounded-lg border border-stone-200 bg-white text-stone-600 text-[11px] font-medium outline-none focus:ring-1 focus:ring-amber-500/40 shrink-0"
                  >
                    {[5, 10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>
                </div>

                {pagedOrders.length === 0 ? (
                  <div className="px-5 py-8 text-center text-stone-500 text-sm">
                    No orders found
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {pagedOrders.map((order, oi) => {
                      const orderCommission = commissions.find(
                        (c) => c.order_id === order.id,
                      );
                      const isOrderExpanded = !!expandedOrders[order.id];

                      return (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: oi * 0.02 }}
                          className="border-b border-stone-100 last:border-b-0 group"
                        >
                          {/* ── Main order row ── */}
                          <div className="flex items-center gap-3 px-5 py-3 hover:bg-stone-50 transition-colors">
                            {/* Expand toggle */}
                            <button
                              onClick={() => toggleOrderExpand(order.id)}
                              className="p-1 rounded-lg text-stone-300 hover:text-amber-500 hover:bg-amber-50 transition-colors shrink-0"
                              title={
                                isOrderExpanded ? "Hide items" : "Show items"
                              }
                            >
                              <ChevronDown
                                size={13}
                                className={`transition-transform duration-200 ${isOrderExpanded ? "rotate-180" : ""}`}
                              />
                            </button>

                            {/* Invoice + customer info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className="font-bold text-stone-800 text-sm">
                                  {order.invoice_number}
                                </p>
                                <span className="text-[10px] text-stone-400">
                                  {formatDate(
                                    order.invoice_date || order.created_at,
                                  )}
                                </span>
                                <span className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded-md">
                                  {order.items?.length || 0} items
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {order.customer_name && (
                                  <span className="text-xs text-stone-600">
                                    {order.customer_name}
                                  </span>
                                )}
                                {order.customer_phone && (
                                  <span className="text-xs text-stone-500 flex items-center gap-1">
                                    <Phone size={9} /> {order.customer_phone}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Order total */}
                            <div className="text-right shrink-0">
                              <p className="font-mono font-black text-stone-800 text-sm">
                                {formatCurrency(order.total)}
                              </p>
                              {order.payment_mode && (
                                <p className="text-[10px] text-stone-400 uppercase">
                                  {order.payment_mode}
                                </p>
                              )}
                            </div>

                            {/* Commission badge + actions */}
                            <div className="flex items-center gap-2 shrink-0">
                              {orderCommission ? (
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${
                                      orderCommission.status === "paid"
                                        ? "bg-emerald-50 border-emerald-200"
                                        : "bg-amber-50 border-amber-200"
                                    }`}
                                  >
                                    {orderCommission.status === "paid" ? (
                                      <CheckCircle2
                                        size={11}
                                        className="text-emerald-600"
                                      />
                                    ) : (
                                      <Clock
                                        size={11}
                                        className="text-amber-600"
                                      />
                                    )}
                                    <span
                                      className={`text-xs font-black ${
                                        orderCommission.status === "paid"
                                          ? "text-emerald-700"
                                          : "text-amber-700"
                                      }`}
                                    >
                                      {formatCurrency(orderCommission.amount)}
                                    </span>
                                  </div>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() =>
                                        handleEditCommission(
                                          order,
                                          orderCommission,
                                        )
                                      }
                                      className="p-1.5 rounded-lg text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                      title="Edit commission"
                                    >
                                      <Edit3 size={12} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        onDeleteCommission(orderCommission.id)
                                      }
                                      className="p-1.5 rounded-lg text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                      title="Delete commission"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleAddCommission(order)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-xs font-bold transition-colors opacity-0 group-hover:opacity-100"
                                >
                                  <Plus size={11} />
                                  Add Commission
                                </button>
                              )}
                            </div>
                          </div>

                          {/* ── Expandable items section ── */}
                          <AnimatePresence>
                            {isOrderExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.18,
                                  ease: "easeInOut",
                                }}
                                className="overflow-hidden"
                              >
                                <div className="mx-5 mb-3 bg-stone-50 border border-stone-200 rounded-xl overflow-hidden">
                                  {/* Items list */}
                                  {(order.items || []).length > 0 ? (
                                    <div className="divide-y divide-stone-100">
                                      {order.items.map((item, ii) => (
                                        <div
                                          key={ii}
                                          className="flex items-center gap-3 px-3 py-2"
                                        >
                                          {/* Item number */}
                                          <span className="text-[10px] text-stone-400 font-mono w-4 shrink-0">
                                            {ii + 1}
                                          </span>

                                          {/* Product name */}
                                          <p className="flex-1 text-xs text-stone-700 font-medium truncate">
                                            {item.product_name}
                                          </p>

                                          {/* GST rate if present */}
                                          {item.gst_rate > 0 && (
                                            <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold shrink-0">
                                              GST {item.gst_rate}%
                                            </span>
                                          )}

                                          {/* HSN if present */}
                                          {item.hsn && (
                                            <span className="text-[10px] text-stone-400 shrink-0">
                                              HSN: {item.hsn}
                                            </span>
                                          )}

                                          {/* Qty × price */}
                                          <span className="text-[11px] text-stone-500 shrink-0 font-mono">
                                            {item.quantity} × ₹
                                            {(item.price || 0).toFixed(2)}
                                          </span>

                                          {/* Item total */}
                                          <span className="text-xs font-black text-stone-800 font-mono w-20 text-right shrink-0">
                                            ₹
                                            {(
                                              item.total ||
                                              item.price * item.quantity
                                            ).toFixed(2)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-stone-400 px-4 py-3">
                                      No item details available
                                    </p>
                                  )}

                                  {/* Order totals footer */}
                                  <div className="border-t border-stone-200 bg-white px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-3 text-[11px] text-stone-500 flex-wrap">
                                      {(order.discount || 0) > 0 && (
                                        <span>
                                          Discount:{" "}
                                          <span className="text-emerald-600 font-semibold">
                                            −₹{order.discount.toFixed(2)}
                                          </span>
                                        </span>
                                      )}
                                      {(order.extra_discount || 0) > 0 && (
                                        <span>
                                          Extra disc:{" "}
                                          <span className="text-amber-600 font-semibold">
                                            −₹{order.extra_discount.toFixed(2)}
                                          </span>
                                        </span>
                                      )}
                                      {(order.transport_charges || 0) > 0 && (
                                        <span>
                                          Transport:{" "}
                                          <span className="text-blue-600 font-semibold">
                                            +₹
                                            {order.transport_charges.toFixed(2)}
                                          </span>
                                        </span>
                                      )}
                                      {(order.tax || 0) > 0 && (
                                        <span>
                                          GST:{" "}
                                          <span className="text-stone-600 font-semibold">
                                            ₹{order.tax.toFixed(2)}
                                          </span>
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-sm font-black text-stone-800">
                                      <span className="text-[10px] text-stone-400 font-normal mr-1">
                                        Sub Total
                                      </span>
                                      ₹{(order.subtotal || 0).toFixed(2)}
                                    </div>
                                    <div className="flex items-center gap-1 text-sm font-black text-stone-800">
                                      <span className="text-[10px] text-stone-400 font-normal mr-1">
                                        Total
                                      </span>
                                      ₹{(order.total || 0).toFixed(2)}
                                    </div>
                                  </div>

                                  {/* Commission note if exists */}
                                  {orderCommission?.notes && (
                                    <div className="border-t border-stone-200 px-3 py-2 bg-amber-50">
                                      <p className="text-[11px] text-amber-700 italic">
                                        Commission note: "
                                        {orderCommission.notes}"
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {totalOrderPages > 1 && (
                  <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between">
                    <span className="text-[11px] text-stone-500">
                      {(orderPage - 1) * orderPageSize + 1}–
                      {Math.min(
                        orderPage * orderPageSize,
                        filteredOrders.length,
                      )}{" "}
                      of {filteredOrders.length} orders
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setOrderPage(1)}
                        disabled={orderPage === 1}
                        className="w-7 h-7 rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-amber-600 hover:bg-stone-50 disabled:opacity-30 flex items-center justify-center transition-all"
                      >
                        <ChevronsLeft size={12} />
                      </button>
                      <button
                        onClick={() => setOrderPage((p) => Math.max(1, p - 1))}
                        disabled={orderPage === 1}
                        className="w-7 h-7 rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-amber-600 hover:bg-stone-50 disabled:opacity-30 flex items-center justify-center transition-all"
                      >
                        <ChevronLeft size={12} />
                      </button>
                      {Array.from(
                        { length: Math.min(5, totalOrderPages) },
                        (_, i) => {
                          const start = Math.max(
                            1,
                            Math.min(orderPage - 2, totalOrderPages - 4),
                          );
                          const p = start + i;
                          return (
                            <button
                              key={p}
                              onClick={() => setOrderPage(p)}
                              className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all ${
                                p === orderPage
                                  ? "bg-amber-500 text-white"
                                  : "bg-white border border-stone-200 text-stone-600 hover:text-amber-600 hover:bg-stone-50"
                              }`}
                            >
                              {p}
                            </button>
                          );
                        },
                      )}
                      <button
                        onClick={() =>
                          setOrderPage((p) => Math.min(totalOrderPages, p + 1))
                        }
                        disabled={orderPage === totalOrderPages}
                        className="w-7 h-7 rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-amber-600 hover:bg-stone-50 disabled:opacity-30 flex items-center justify-center transition-all"
                      >
                        <ChevronRight size={12} />
                      </button>
                      <button
                        onClick={() => setOrderPage(totalOrderPages)}
                        disabled={orderPage === totalOrderPages}
                        className="w-7 h-7 rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-amber-600 hover:bg-stone-50 disabled:opacity-30 flex items-center justify-center transition-all"
                      >
                        <ChevronsRight size={12} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="px-5 py-3 bg-stone-50 border-t border-stone-100 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    {[
                      {
                        label: "Total Revenue",
                        value: formatCurrency(totalRevenue),
                        color: "text-stone-700",
                      },
                      {
                        label: "Total Commission",
                        value: formatCurrency(totalEarned),
                        color: "text-amber-600",
                      },
                      {
                        label: "Pending",
                        value: formatCurrency(pendingAmount),
                        color: "text-amber-500",
                      },
                      {
                        label: "Paid",
                        value: formatCurrency(paidAmount),
                        color: "text-emerald-600",
                      },
                    ].map((s) => (
                      <div key={s.label}>
                        <p className="text-[9px] text-stone-500 uppercase tracking-wider font-bold">
                          {s.label}
                        </p>
                        <p className={`text-sm font-black ${s.color}`}>
                          {s.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {showCommissionModal && selectedOrder && (
          <CommissionModal
            order={selectedOrder}
            staff={userDetails}
            existingCommission={editingCommission}
            onClose={() => {
              setShowCommissionModal(false);
              setSelectedOrder(null);
              setEditingCommission(null);
            }}
            onSave={async (data) => {
              if (editingCommission) {
                await onEditCommission(editingCommission.id, data);
              } else {
                await onAddCommission(data);
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
const StaffCommissionManager = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canManage = isAdmin || isManager;

  const [orders, setOrders] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState(null);

  // ── Month filter (default = current month) ────────────────
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());

  // Staff-level pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // ── Fetch data with month-aware query params ──────────────
  const fetchData = useCallback(async () => {
    if (!canManage) {
      toast.error("Access denied");
      return;
    }
    setLoading(true);
    try {
      // Build order date params
      const orderParams = new URLSearchParams({ limit: "10000" });
      const commissionParams = new URLSearchParams();

      if (selectedMonth) {
        const [year, month] = selectedMonth.split("-");
        const fromDate = `${selectedMonth}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const toDate = `${selectedMonth}-${String(lastDay).padStart(2, "0")}`;

        orderParams.set("date_from", fromDate);
        orderParams.set("date_to", toDate);
        commissionParams.set("date_from", fromDate);
        commissionParams.set("date_to", toDate);
      }

      const [ordersRes, commissionsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/orders?${orderParams.toString()}`, { headers }),
        axios.get(`${API_URL}/commissions?${commissionParams.toString()}`, {
          headers,
        }),
        axios.get(`${API_URL}/users`, { headers }),
      ]);

      setOrders(ordersRes.data?.orders || ordersRes.data || []);
      setCommissions(commissionsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [headers, canManage, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCommissionsOnly = async () => {
    try {
      const commissionParams = new URLSearchParams();
      if (selectedMonth) {
        const [year, month] = selectedMonth.split("-");
        const fromDate = `${selectedMonth}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const toDate = `${selectedMonth}-${String(lastDay).padStart(2, "0")}`;
        commissionParams.set("date_from", fromDate);
        commissionParams.set("date_to", toDate);
      }
      const res = await axios.get(`${API_URL}/commissions?${commissionParams.toString()}`, { headers });
      setCommissions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, itemsPerPage, selectedMonth]);

  // Group orders by referred staff
  const groupedByStaff = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      if (order.referred_by_staff_id) {
        const id = order.referred_by_staff_id;
        if (!map[id]) {
          map[id] = {
            id,
            name: order.referred_by_staff_name,
            orders: [],
          };
        }
        map[id].orders.push(order);
      }
    });
    return Object.values(map);
  }, [orders]);

  const filteredStaff = useMemo(() => {
    return groupedByStaff.filter((staff) => {
      const u = users.find((u) => u.id === staff.id);
      const matchSearch =
        !searchQuery ||
        staff.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u?.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchRole = !roleFilter || u?.role === roleFilter;
      return matchSearch && matchRole;
    });
  }, [groupedByStaff, searchQuery, roleFilter, users]);

  const sortedStaff = useMemo(() => {
    return [...filteredStaff].sort((a, b) => {
      const aTotal = commissions
        .filter((c) => c.staff_id === a.id)
        .reduce((s, c) => s + c.amount, 0);
      const bTotal = commissions
        .filter((c) => c.staff_id === b.id)
        .reduce((s, c) => s + c.amount, 0);
      return bTotal - aTotal;
    });
  }, [filteredStaff, commissions]);

  const totalPages = Math.ceil(sortedStaff.length / itemsPerPage);
  const pagedStaff = sortedStaff.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const summary = useMemo(() => {
    const totalRevenue = orders.reduce((s, o) => s + o.total, 0);
    const totalCommission = commissions.reduce((s, c) => s + c.amount, 0);
    const pendingCommission = commissions
      .filter((c) => c.status === "pending")
      .reduce((s, c) => s + c.amount, 0);
    const paidCommission = commissions
      .filter((c) => c.status === "paid")
      .reduce((s, c) => s + c.amount, 0);
    return {
      totalStaff: groupedByStaff.length,
      totalOrders: orders.filter((o) => o.referred_by_staff_id).length,
      totalRevenue,
      totalCommission,
      pendingCommission,
      paidCommission,
    };
  }, [groupedByStaff, orders, commissions]);

  // Handlers
  const handleAddCommission = async (data) => {
    try {
      await axios.post(`${API_URL}/commissions`, data, { headers });
      toast.success("Commission added successfully!");
      fetchCommissionsOnly();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add commission");
      throw err;
    }
  };

  const handleDeleteCommission = async (id) => {
    if (!window.confirm("Delete this commission entry?")) return;
    try {
      await axios.delete(`${API_URL}/commissions/${id}`, { headers });
      toast.success("Commission deleted");
      fetchCommissionsOnly();
    } catch {
      toast.error("Failed to delete commission");
    }
  };

  const handleEditCommission = async (id, data) => {
    try {
      await axios.put(`${API_URL}/commissions/${id}`, data, { headers });
      toast.success("Commission updated!");
      fetchCommissionsOnly();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update commission");
      throw err;
    }
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-full bg-[#f7f6f3]">
        <div className="text-center">
          <div className="p-4 bg-rose-100 rounded-2xl mb-4 inline-block">
            <X size={32} className="text-rose-500" />
          </div>
          <p className="text-stone-600 font-semibold">Access Denied</p>
          <p className="text-stone-500 text-sm mt-1">
            Only admins and managers can access this page
          </p>
        </div>
      </div>
    );
  }

  // Friendly label for the header
  const monthLabel = selectedMonth
    ? new Date(selectedMonth + "-01").toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      })
    : "All Time";

  return (
    <div className="min-h-full bg-[#f7f6f3] p-4 md:p-6 lg:p-8">
      {/* ── Hero Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-[#1c1c2e] rounded-2xl p-6 mb-6 shadow-xl"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 40%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 15% 80%, #8b5cf6 0%, transparent 40%)",
          }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/15 rounded-2xl border border-amber-500/20">
              <Users size={24} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                Staff Commission Manager
              </h1>
              <p className="text-stone-400 text-sm mt-0.5">
                Manage commissions by referred staff member
                {selectedMonth && (
                  <span className="ml-2 text-amber-400 font-semibold">
                    — {monthLabel}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-stone-300 hover:text-white font-bold text-sm px-4 py-3 rounded-xl transition-all"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Summary stats */}
        <div className="relative mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            {
              label: "Staff Members",
              value: summary.totalStaff,
              color: "text-amber-400",
            },
            {
              label: "Total Orders",
              value: summary.totalOrders,
              color: "text-blue-400",
            },
            {
              label: "Total Revenue",
              value: formatCurrency(summary.totalRevenue),
              color: "text-emerald-400",
            },
            {
              label: "Total Commission",
              value: formatCurrency(summary.totalCommission),
              color: "text-purple-400",
            },
            {
              label: "Pending",
              value: formatCurrency(summary.pendingCommission),
              color: "text-amber-300",
            },
            {
              label: "Paid",
              value: formatCurrency(summary.paidCommission),
              color: "text-emerald-300",
            },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                {s.label}
              </p>
              <p className={`font-mono font-black text-lg ${s.color}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Filters (including Month Picker) ── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-5 flex flex-wrap gap-3 items-center"
      >
        {/* Month Picker — first, most important filter */}
        <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />

        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={15}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff by name or email…"
            className="w-full h-11 bg-white border border-stone-200 rounded-xl pl-10 pr-4 text-sm text-stone-700 outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400/50 shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 p-1"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Role filter */}
        <select
          value={roleFilter || ""}
          onChange={(e) => setRoleFilter(e.target.value || null)}
          className="h-11 px-4 rounded-xl border border-stone-200 bg-white text-stone-600 text-sm outline-none focus:ring-2 focus:ring-amber-500/30 shadow-sm"
        >
          <option value="">All Roles</option>
          <option value="staff">Staff</option>
          <option value="billing">Billing</option>
          <option value="manager">Manager</option>
        </select>

        {(searchQuery || roleFilter) && (
          <button
            onClick={() => {
              setSearchQuery("");
              setRoleFilter(null);
            }}
            className="h-11 px-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-sm font-bold flex items-center gap-1.5 hover:bg-rose-100 transition-colors"
          >
            <X size={13} />
            Clear
          </button>
        )}
      </motion.div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="relative w-14 h-14 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-amber-100" />
              <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
            </div>
            <p className="text-stone-400 text-sm">Loading staff data…</p>
          </div>
        </div>
      ) : pagedStaff.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-stone-100 shadow-sm">
          <div className="p-4 bg-stone-100 rounded-2xl mb-4">
            <Users size={32} className="text-stone-300" />
          </div>
          <p className="text-stone-500 font-semibold">No staff members found</p>
          <p className="text-stone-400 text-sm mt-1">
            {searchQuery
              ? "Try adjusting your search"
              : selectedMonth
                ? `No referred orders in ${monthLabel}`
                : "No orders with referred staff yet"}
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          <div className="space-y-3">
            {pagedStaff.map((staff, idx) => (
              <StaffCard
                key={staff.id}
                staff={staff}
                orders={staff.orders}
                commissions={commissions}
                onAddCommission={handleAddCommission}
                onDeleteCommission={handleDeleteCommission}
                onEditCommission={handleEditCommission}
                index={idx}
                allUsers={users}
              />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={sortedStaff.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </motion.div>
      )}
    </div>
  );
};

export default StaffCommissionManager;
